const REDACTED_VALUE = '[REDACTED]';

const BEARER_TOKEN_PATTERN = /\bBearer\s+[A-Z0-9\-._~+/=]+\b/gi;
const JWT_PATTERN =
  /\b[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}\b/g;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const prototype = Reflect.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const normalizeSensitiveKey = (key: string) =>
  key.replaceAll(/[^a-zA-Z0-9]/g, '').toLowerCase();

const isSensitiveKey = (key: string) => {
  const normalizedKey = normalizeSensitiveKey(key);
  return (
    normalizedKey === 'authorization' ||
    normalizedKey === 'cookie' ||
    normalizedKey === 'cookies' ||
    normalizedKey === 'setcookie' ||
    normalizedKey === 'code' ||
    normalizedKey === 'jwt' ||
    normalizedKey === 'launch' ||
    normalizedKey.endsWith('token')
  );
};

type ErrorWithCause = Error & { cause?: unknown };

const sanitizeSensitiveString = (value: string) => {
  let sanitizedValue = value.replaceAll(BEARER_TOKEN_PATTERN, REDACTED_VALUE);
  sanitizedValue = sanitizedValue.replaceAll(JWT_PATTERN, REDACTED_VALUE);
  return sanitizedValue;
};

const sanitizeError = (error: ErrorWithCause): Record<string, unknown> => {
  const sanitizedError: Record<string, unknown> = {
    name: sanitizeSensitiveString(error.name),
    message: sanitizeSensitiveString(error.message),
  };
  if (error.stack) {
    sanitizedError.stack = sanitizeSensitiveString(error.stack);
  }
  if (error.cause !== undefined) {
    sanitizedError.cause = sanitizeDatadogValue(error.cause);
  }
  for (const [key, nestedValue] of Object.entries(error)) {
    if (
      key === 'name' ||
      key === 'message' ||
      key === 'stack' ||
      key === 'cause'
    ) {
      continue;
    }
    sanitizedError[key] = isSensitiveKey(key)
      ? REDACTED_VALUE
      : sanitizeDatadogValue(nestedValue);
  }
  return sanitizedError;
};

const sanitizeDatadogValue = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return sanitizeSensitiveString(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeDatadogValue(item));
  }
  if (value instanceof Error) {
    return sanitizeError(value);
  }
  if (isPlainObject(value)) {
    const sanitizedValue: Record<string, unknown> = {};
    for (const [key, nestedValue] of Object.entries(value)) {
      sanitizedValue[key] = isSensitiveKey(key)
        ? REDACTED_VALUE
        : sanitizeDatadogValue(nestedValue);
    }
    return sanitizedValue;
  }
  return value;
};

/** Sanitize a Datadog event in-place before it is sent. */
export function sanitizeDatadogEvent(event: unknown): void {
  if (!isPlainObject(event)) return;
  for (const [key, value] of Object.entries(event)) {
    event[key] = isSensitiveKey(key)
      ? REDACTED_VALUE
      : sanitizeDatadogValue(value);
  }
}

/** Sanitize a string by redacting Bearer tokens and JWTs. */
export { sanitizeSensitiveString };
